import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FlyFreelyConstants } from '../constants';
import {
    AssignMaintenanceActivityCommand,
    BulkCreateMaintenanceLogCommand,
    CancelMaintenanceActivityCommand,
    CancelMaintenanceLogCommand,
    CompleteMaintenanceLogCommand,
    CreateFlightTestMissionCommand,
    CreateMaintenanceActivityCommand,
    CreateMaintenanceLogCommand,
    FinaliseSubmittedMaintenanceActivityCommand,
    LinkMissionAsFlightTestCommand,
    MaintenanceActivityDto,
    MaintenanceLogDto,
    MaintenanceLogSummary,
    ReassignMaintenanceActivityCommand,
    ResultDto,
    StartMaintenanceProgressCommand,
    StopMaintenanceCommand,
    SubmitMaintenanceActivityCommand,
    SubmitMaintenanceLogCommand,
    TransferMaintenanceLogCommand,
    TriggerActivationDto,
    UnfinaliseMaintenanceActivityCommand,
    UnfinaliseMaintenanceLogCommand,
    UnlinkMissionAsFlightTestCommand,
    UpdateAssignedMaintenanceActivityCommand,
    UpdateSubmittedMaintenanceActivityCommand,
    UpdateSubmittedMaintenanceLogCommand,
    UpdateUnassignedMaintenanceActivityCommand,
    UpdateUnsubmittedMaintenanceLogCommand
} from '../model/api';
import { NameValue } from '../model/ui';
import { AttachmentHandler } from './attachments';
import { httpParamSerializer } from './service.helpers';

@Injectable({
    providedIn: 'root'
})
export class MaintenanceService {
    private baseUrl: string;
    private changeSource = new Subject<void>();
    change$ = this.changeSource.asObservable();

    constructor(constants: FlyFreelyConstants, private http: HttpClient) {
        this.baseUrl = constants.API_URL;
    }

    ngOnDestroy() {
        this.changeSource.complete();
    }

    attachmentHandler(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        managingOrganisationId: number
    ) {
        return new AttachmentHandler(
            this.http,
            `/webapi/maintenance/${maintenanceLogId}` +
                `/activities/${maintenanceActivityId}/attachments`,
            undefined,
            managingOrganisationId
        );
    }

    findMaintenanceLogs(
        organisationId: number,
        textSearch?: string | null,
        resourceCategory?: string,
        resourceId?: number,
        airworthinessNoticeId?: number
    ) {
        return this.http.get<MaintenanceLogSummary[]>(
            `${this.baseUrl}/webapi/maintenance`,
            {
                params: httpParamSerializer({
                    organisationId,
                    textSearch,
                    resourceCategory,
                    resourceId,
                    airworthinessNoticeId
                })
            }
        );
    }

    createMaintenanceLog(command: CreateMaintenanceLogCommand) {
        return this.http.post<MaintenanceLogDto>(
            `${this.baseUrl}/webapi/maintenance`,
            command
        );
        // .pipe(tap(() => this.changeSource.next()));
    }
    createBulkMaintenanceRequest(command: BulkCreateMaintenanceLogCommand) {
        return this.http
            .post<BulkCreateMaintenanceLogCommand>(
                `${this.baseUrl}/webapi/maintenance/bulk`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    findMaintenanceLogById(
        maintenanceLogId: number,
        managingOrganisationId: number
    ) {
        return this.http.get<MaintenanceLogDto>(
            `${this.baseUrl}/webapi/maintenance/${maintenanceLogId}`,
            {
                params: httpParamSerializer({ managingOrganisationId })
            }
        );
    }

    updateMaintenanceLog(
        maintenanceLogId: number,
        command: UpdateUnsubmittedMaintenanceLogCommand
    ) {
        return this.http
            .put<MaintenanceLogDto>(
                `${this.baseUrl}/webapi/maintenance/${maintenanceLogId}`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    deleteMaintenanceLog(
        maintenanceLogId: number,
        managingOrganisationId: number
    ) {
        return this.http
            .delete<void>(
                `${this.baseUrl}/webapi/maintenance/${maintenanceLogId}`,
                { params: httpParamSerializer({ managingOrganisationId }) }
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    transferMaintenanceLog(
        maintenanceLogId: number,
        command: TransferMaintenanceLogCommand
    ) {
        return this.http
            .put<MaintenanceLogDto>(
                `${this.baseUrl}/webapi/maintenance/${maintenanceLogId}/transfer`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    updateMaintenanceIdentifierAndWorkInstructionsUrl(
        maintenanceLogId: number,
        command: UpdateSubmittedMaintenanceLogCommand
    ) {
        return this.http
            .put<MaintenanceLogDto>(
                `${this.baseUrl}/webapi/maintenance/${maintenanceLogId}/details`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    createMaintenanceActivity(
        maintenanceLogId: number,
        command: CreateMaintenanceActivityCommand
    ) {
        return this.http
            .post<MaintenanceActivityDto>(
                `${this.baseUrl}/webapi/maintenance/${maintenanceLogId}/activities`,
                command
            )
            .pipe(tap(() => this.changeSource.next()));
    }

    findMaintenanceActivityById(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        managingOrganisationId: number
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}`;
        return this.http.get<MaintenanceActivityDto>(`${this.baseUrl}${url}`, {
            params: httpParamSerializer({ managingOrganisationId })
        });
    }

    updateUnassignedMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: UpdateUnassignedMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    deleteMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        managingOrganisationId: number
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}`;
        return this.http.delete<void>(`${this.baseUrl}${url}`, {
            params: httpParamSerializer({ managingOrganisationId })
        });
    }

    assignMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: AssignMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/assign`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    submitMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: SubmitMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/submit`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    updateAssignedMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: UpdateAssignedMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/work`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    updateSubmittedMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: UpdateSubmittedMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/review`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    finaliseSubmittedMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: FinaliseSubmittedMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/finalise`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    createFlightTest(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: CreateFlightTestMissionCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/flightTest`;
        return this.http.post<MaintenanceActivityDto>(
            `${this.baseUrl}${url}`,
            command
        );
    }

    linkFlightTest(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: LinkMissionAsFlightTestCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/linkFlightTest`;
        return this.http.post<MaintenanceActivityDto>(
            `${this.baseUrl}${url}`,
            command
        );
    }

    unlinkFlightTest(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: UnlinkMissionAsFlightTestCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/unlinkFlightTest`;
        return this.http.post<MaintenanceActivityDto>(
            `${this.baseUrl}${url}`,
            command
        );
    }

    checkIdentifierUnique(organisationId: number, identifier: string) {
        const url = `/webapi/maintenance/checkIdentifier`;
        return this.http.get<ResultDto>(`${this.baseUrl}${url}`, {
            params: httpParamSerializer({
                organisationId: organisationId,
                identifier: identifier
            })
        });
    }

    submitMaintenanceLog(
        maintenanceLogId: number,
        command: SubmitMaintenanceLogCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/submit`;
        return this.http
            .put<MaintenanceLogDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    finaliseMaintenanceLog(
        maintenanceLogId: number,
        command: CompleteMaintenanceLogCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/finalise`;
        return this.http
            .put<MaintenanceLogDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    unfinaliseMaintenanceLog(
        maintenanceLogId: number,
        command: UnfinaliseMaintenanceLogCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/unfinalise`;
        return this.http
            .put<MaintenanceLogDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    startMaintenanceActivityProgress(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: StartMaintenanceProgressCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/start`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    stopMaintenanceActivityProgress(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: StopMaintenanceCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/stop`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    cancelMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: CancelMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/cancel`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    cancelMaintenanceLog(
        maintenanceLogId: number,
        command: CancelMaintenanceLogCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/cancel`;
        return this.http
            .put<MaintenanceLogDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    reassignMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: ReassignMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/reassign`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    unfinaliseMaintenanceActivity(
        maintenanceLogId: number,
        maintenanceActivityId: number,
        command: UnfinaliseMaintenanceActivityCommand
    ) {
        const url = `/webapi/maintenance/${maintenanceLogId}/activities/${maintenanceActivityId}/unfinalise`;
        return this.http
            .put<MaintenanceActivityDto>(`${this.baseUrl}${url}`, command)
            .pipe(tap(() => this.changeSource.next()));
    }

    exportOrganisationMaintenance(organisationId: number) {
        const headers = new HttpHeaders({
            'Content-type': 'text/csv;charset=utf-8',
            Accept: 'text/csv;charset=utf-8'
        });

        return this.http.get(`${this.baseUrl}/webapi/maintenance/export`, {
            params: httpParamSerializer({
                organisationId
            }),
            responseType: 'blob',
            headers: headers
        });
    }

    getEquipmentTypes(): NameValue[] {
        return [
            {
                value: 'CRAFT',
                name: 'RPA'
            },
            {
                value: 'BATTERY',
                name: 'Battery'
            },
            {
                value: 'BATTERY_SET',
                name: 'Battery Set'
            },
            { value: 'EQUIPMENT', name: 'Equipment' }
        ];
    }

    getMaintenanceLogStatuses(): NameValue[] {
        return [
            { value: 'DRAFT', name: 'Draft' },
            { value: 'REQUESTED', name: 'Requested' },
            { value: 'COMPLETED', name: 'Completed' },
            { value: 'CANCELLED', name: 'Cancelled' }
        ];
    }

    getMaintenanceActivityStatuses(): NameValue[] {
        return [
            { value: 'ASSIGNED', name: 'Assigned' },
            { value: 'CANCELLED', name: 'Cancelled' },
            { value: 'COMPLETED', name: 'Completed' },
            { value: 'DRAFT', name: 'Draft' },
            { value: 'IN_PROGRESS', name: 'In Progress' },
            { value: 'SUBMITTED', name: 'Submitted' }
        ];
    }

    getCombinedStatusFilters(): NameValue[] {
        return [
            { value: 'REQUESTED|ASSIGNED|ACTIVITY_COMPLETED', name: 'Pending' },
            { value: 'DRAFT', name: 'Draft' },
            { value: 'REQUESTED', name: 'Requested' },
            { value: 'COMPLETED', name: 'Finalised' },
            { value: 'ASSIGNED', name: 'Assigned' },
            { value: 'CANCELLED', name: 'Cancelled' },
            { value: 'ACTIVITY_COMPLETED', name: 'Activity Completed' },
            { value: 'ACTIVITY_CANCELLED', name: 'Activity Cancelled' }
        ];
    }

    getCombinedStatuses(): NameValue[] {
        return [
            { value: 'DRAFT', name: 'Draft' },
            { value: 'REQUESTED', name: 'Requested' },
            { value: 'COMPLETED', name: 'Finalised' },
            { value: 'ASSIGNED', name: 'Assigned' },
            { value: 'CANCELLED', name: 'Cancelled' },
            { value: 'ACTIVITY_COMPLETED', name: 'Activity Completed' },
            { value: 'ACTIVITY_CANCELLED', name: 'Activity Cancelled' }
        ];
    }

    getCombinedStatus(logStatus: string, activityStatus: string): string {
        if (logStatus === 'DRAFT') {
            return 'DRAFT';
        } else if (logStatus === 'COMPLETED') {
            return 'COMPLETED';
        } else if (logStatus === 'CANCELLED') {
            return 'CANCELLED';
        } else if (logStatus === 'REQUESTED') {
            if (activityStatus === 'DRAFT') {
                return 'REQUESTED';
            } else if (
                activityStatus === 'ASSIGNED' ||
                activityStatus === 'IN_PROGRESS'
            ) {
                return 'ASSIGNED';
            } else if (activityStatus === 'SUBMITTED') {
                return 'ACTIVITY_COMPLETED';
            } else if (activityStatus === 'COMPLETED') {
                return 'REQUESTED';
            } else if (
                activityStatus === 'REQUESTED' ||
                activityStatus === 'CANCELLED'
            ) {
                return 'ACTIVITY_CANCELLED';
            } else {
                return 'REQUESTED';
            }
        }
    }

    getMaintenanceLogTypes(): NameValue[] {
        return [
            {
                name: 'Defect',
                value: 'DEFECT'
            },
            {
                name: 'Scheduled',
                value: 'SCHEDULED'
            },
            {
                name: 'In Field',
                value: 'IN_FIELD'
            },
            {
                name: 'Inspection',
                value: 'INSPECTION'
            },
            {
                name: 'Configuration Change',
                value: 'CONFIGURATION_CHANGE'
            },
            {
                name: 'Other',
                value: 'OTHER'
            }
        ];
    }
}

export const maintenanceOutcomes = Object.freeze([
    {
        value: MaintenanceLogDto.Outcome.RELEASE_TO_SERVICE,
        name: 'Release to Service'
    },
    {
        value: MaintenanceLogDto.Outcome.RETIRE_FROM_SERVICE,
        name: 'Retire from Service'
    }
]);

export const triggerActivationTypes = Object.freeze([
    { name: 'Due', value: TriggerActivationDto.Type.DUE },
    { name: 'Upcoming', value: TriggerActivationDto.Type.UPCOMING },
    { name: 'Overdue', value: TriggerActivationDto.Type.OVERDUE }
]);
